import React, { useEffect, useCallback } from 'react';
import { Drawer, Form, Button, Space, Spin, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MemberTypeForm from './MemberTypeForm';
import {
  getMemberTypeDetail,
  createMemberType,
  updateMemberType,
} from '../api';
import { MemberTypeFormData, MemberTypeRecord, PaymentType, MemberLevel } from '../types';
import { QUERY_KEYS, DRAWER_CONFIG, FORM_DEFAULT_VALUES } from '../constants';

interface MemberTypeDrawerProps {
  open: boolean;
  editingMemberType?: MemberTypeRecord | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const MemberTypeDrawer: React.FC<MemberTypeDrawerProps> = ({
  open,
  editingMemberType,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<MemberTypeFormData>();
  const isEdit = !!editingMemberType?.id;
  const memberTypeId = editingMemberType?.id;

  // 获取详情
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: [QUERY_KEYS.MEMBER_TYPE_DETAIL, memberTypeId],
    queryFn: () => getMemberTypeDetail(memberTypeId!),
    enabled: isEdit && !!memberTypeId,
  });

  // 创建
  const createMutation = useMutation({
    mutationFn: createMemberType,
    onSuccess: () => {
      message.success('创建成功');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MEMBER_TYPE_LIST] });
      handleClose();
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('创建失败:', error);
      message.error(error.message || '创建失败');
    },
  });

  // 更新
  const updateMutation = useMutation({
    mutationFn: (data: MemberTypeFormData) => updateMemberType(memberTypeId!, data),
    onSuccess: () => {
      message.success('更新成功');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MEMBER_TYPE_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MEMBER_TYPE_DETAIL, memberTypeId] });
      handleClose();
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('更新失败:', error);
      message.error(error.message || '更新失败');
    },
  });

  // 加载详情数据到表单
  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (detailData && isEdit) {
      // 回显时，需要将后端返回的 level 转换为表单显示值
      // level = 3 且 type = 1 (Google Pay) -> Lite (3)
      // level = 2 且 type = 0 (Apple Pay) -> Lite (3)
      // level = 2 且 type = 1 (Google Pay) -> Basic (2)
      let displayLevel = detailData.level;
      if (detailData.level === 3 && detailData.type === PaymentType.GOOGLE_PAY) {
        displayLevel = MemberLevel.LITE;
      } else if (detailData.level === 2 && detailData.type === PaymentType.APPLE_PAY) {
        displayLevel = MemberLevel.LITE;
      } else if (detailData.level === 2 && detailData.type === PaymentType.GOOGLE_PAY) {
        displayLevel = MemberLevel.BASIC;
      }

      const formValues: MemberTypeFormData = {
        productId: detailData.productId || '',
        amount: detailData.amount || 0,
        originalAmount: detailData.originalAmount || 0,
        discount: detailData.discount || '',
        discountDescEn: detailData.discountDescEn || '',
        discountDescJa: detailData.discountDescJa || '',
        type: detailData.type,
        periodType: detailData.periodType,
        level: displayLevel,
        status: detailData.status ?? 1,
        privilegeIds: detailData.privilegeIds || [],
        memberNameEn: detailData.memberNameEn || '',
        memberNameJa: detailData.memberNameJa || '',
        memberDescEn: detailData.memberDescEn || '',
        memberDescJa: detailData.memberDescJa || '',
        amountDescEn: detailData.amountDescEn || '',
        amountDescJa: detailData.amountDescJa || '',
        activityDescEn: detailData.activityDescEn || '',
        activityDescJa: detailData.activityDescJa || '',
        limitStatus: detailData.limitStatus ?? 0,
        limitIconEn: detailData.limitIconEn || '',
        limitIconJa: detailData.limitIconJa || '',
        recommendStatus: detailData.recommendStatus ?? 0,
        recommendIconEn: detailData.recommendIconEn || '',
        recommendIconJa: detailData.recommendIconJa || '',
      };

      form.setFieldsValue(formValues);
    } else {
      form.resetFields();
      form.setFieldsValue(FORM_DEFAULT_VALUES);
    }
  }, [detailData, isEdit, open, form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      
      // 如果选择 Lite 等级，根据类型判断传值
      // Google Pay (type = 1) 传 3，Apple Pay (type = 0) 传 2
      if (values.level === MemberLevel.LITE) {
        if (values.type === PaymentType.GOOGLE_PAY) {
          values.level = 3 as any; // Google Pay 传 3
        } else if (values.type === PaymentType.APPLE_PAY) {
          values.level = 2 as any; // Apple Pay 传 2
        }
      }
      
      const mutation = isEdit ? updateMutation : createMutation;
      mutation.mutate(values);
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error('请检查表单填写是否正确');
    }
  }, [form, isEdit, updateMutation, createMutation]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const loading = detailLoading || createMutation.isPending || updateMutation.isPending;

  return (
    <Drawer
      title={isEdit ? '编辑会员类型' : '新增会员类型'}
      width={DRAWER_CONFIG.WIDTH}
      onClose={handleClose}
      open={open}
      placement={DRAWER_CONFIG.PLACEMENT}
      maskClosable={false}
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Space>
          <Button onClick={handleClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            {isEdit ? '保存' : '创建'}
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <MemberTypeForm form={form} />
      </Spin>
    </Drawer>
  );
};

export default MemberTypeDrawer;

